<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import {
  FAIR_PAGE_LOADER_DRAW_MS,
  FAIR_PAGE_LOADER_MONOGRAM_PATH,
} from '@/ui/tokens/fair-page-loader.data'

interface FairPageLoaderMarkProps {
  /** Restart the draw loop (e.g. each time overlay opens). */
  replayKey?: number | string
  size?: number
}

const props = withDefaults(defineProps<FairPageLoaderMarkProps>(), {
  replayKey: 0,
  size: 144,
})

const pathRef = ref<SVGPathElement | null>(null)
const pathLength = ref(0)
const ready = ref(false)

const markStyle = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
  '--fair-page-loader-length': String(pathLength.value),
  '--fair-page-loader-draw-ms': `${FAIR_PAGE_LOADER_DRAW_MS}ms`,
}))

async function measurePath() {
  await nextTick()
  const el = pathRef.value
  if (!el) return
  pathLength.value = el.getTotalLength()
  ready.value = true
}

onMounted(() => {
  void measurePath()
})

watch(
  () => props.replayKey,
  async () => {
    ready.value = false
    await measurePath()
  },
)
</script>

<template>
  <div
    class="fair-page-loader__mark"
    :class="{ 'fair-page-loader__mark--ready': ready }"
    :style="markStyle"
  >
    <svg
      class="fair-page-loader__svg"
      viewBox="0 0 57.2887 57.2887"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        ref="pathRef"
        class="fair-page-loader__path"
        :d="FAIR_PAGE_LOADER_MONOGRAM_PATH"
        :style="
          pathLength
            ? {
                strokeDasharray: pathLength,
                // Same value used by keyframes via inherited CSS var
              }
            : undefined
        "
      />
    </svg>
  </div>
</template>
